<template>
    <div class="payment-page">
        <div class="payment-card">

            <div class="green-header">

                <div class="header-top">
                    <div>
                        <p class="small-title">🛡 Secure Payment</p>
                        <h2>₹{{ amount.toFixed(2) }}</h2>
                        <p class="recharge-text">Recharge Amount</p>
                    </div>

                    <div class="timer">
                        <i class="bi bi-clock"></i>
                        {{ formattedTimer }}
                        <small>Time Remaining</small>
                    </div>
                </div>

                <div class="steps header-steps">
                    <div class="active">
                        <b>1</b>
                        <span>Pay</span>
                    </div>

                    <div>
                        <b>2</b>
                        <span>Pay via</span>
                    </div>

                    <div>
                        <b>3</b>
                        <span>Submit UTR</span>
                    </div>
                </div>

            </div>

            <div class="payment-box qr-section">
                <h4>Scan & Pay</h4>

                <p class="qr-subtitle">
                    Scan the QR code using any UPI app
                </p>

                <img v-if="qrCodeUrl" :src="qrCodeUrl" class="qr-image" />

                <p class="qr-warning">
                    Do not reuse the same QR code for multiple payments.
                </p>
            </div>

            <div v-if="false" class="payment-box">
                <h4>UPI Apps</h4>

                <button class="upi-btn" @click="openUPIApp()">
                    <div class="upi-left">
                        <img src="../assets/images/paytm.jpg" alt="Paytm">
                        <div>
                            <strong>Pay with Paytm</strong>
                            <small>Fast & Secure Payment</small>
                        </div>
                    </div>
                    <span>›</span>
                </button>

                <button class="upi-btn" @click="openUPIApp()">
                    <div class="upi-left">
                        <img src="../assets/images/phonepay.jpg" alt="PhonePe">
                        <div>
                            <strong>Pay with PhonePe</strong>
                            <small>Instant UPI Payment</small>
                        </div>
                    </div>
                    <span>›</span>
                </button>

                <div class="or-divider">
                    <span></span>
                    <b>OR</b>
                    <span></span>
                </div>

                <button class="upi-btn" @click="openUPIApp()">
                    <div class="upi-left">
                        <img src="../assets/images/gpay.jpg" alt="Google Pay">
                        <div>
                            <strong>Pay with Google Pay</strong>
                            <small>Pay using Google Pay</small>
                        </div>
                    </div>
                    <span>›</span>
                </button>

            </div>

            <div class="payment-box">
                <h4>UPI Details</h4>

                <div class="copy-row">
                    <div class="copy-info">
                        <small>UPI ID</small>
                        <strong>{{ upiId }}</strong>
                    </div>
                    <button @click="copyToClipboard(upiId)">Copy</button>
                </div>

                <div class="copy-row">
                    <div class="copy-info">
                        <small>Amount</small>
                        <strong>₹{{ amount.toFixed(2) }}</strong>
                    </div>
                    <button @click="copyToClipboard(amount.toFixed(2))">Copy</button>
                </div>
            </div>

            <div class="utr-box">

                <h4>Submit UTR</h4>

                <p class="utr-desc">
                    After successful payment, enter your UTR number below to verify your transaction.
                </p>

                <input
                    type="text"
                    v-model="utr"
                    maxlength="12"
                    placeholder="Enter UTR (12 digits)"
                />

                <button class="submit-btn" @click="submitUTR">
                    Submit UTR
                </button>

                

            </div>

        </div>

        <div class="notice-box">
            <h4>ⓘ Notice</h4>

            <ol>
                <li>1. Please make sure the payment amount matches your recharge amount.</li>
                <li>2. Please select the correct payment method and complete the payment.</li>
                <li>3. Enter the correct 12 digit UTR number after successful payment.</li>
                <li>4. After submitting UTR, please wait for verification. Do not submit duplicate requests.</li>
            </ol>
        </div>

        <div v-if="showUtrPopup" class="utr-overlay">
            <div class="utr-popup">

                <h3>Use Mobile Scan code to pay</h3>

                <img v-if="qrCodeUrl" :src="qrCodeUrl" class="utr-qr" />

                <div class="utr-timer">
                    {{ formattedTimer }}
                </div>

                <p>
                    After successful payment, enter your UTR number below to verify your transaction.<br>
                    सफल भुगतान के बाद अपने ट्रांजैक्शन को वेरिफाई करने के लिए नीचे अपना UTR नंबर दर्ज करें।
                </p>

                <input
                    type="text"
                    v-model="utr"
                    maxlength="12"
                    placeholder="Enter UTR (12 digits)"
                />

                <button @click="submitUTR">
                    Submit
                </button>

            </div>
        </div>

        <div v-if="toast.show" class="toast-popup">
            {{ toast.message }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import axios from "axios";

// State
const router = useRouter();
const route = useRoute();

const timer = ref(600); // 10 mins
const amount = ref(0);
const upiId = ref("");
const qrCodeUrl = ref("");
const utr = ref("");
const showUtrPopup = ref(false);

const toast = ref({ show: false, message: "" });

// Computed timer
const formattedTimer = computed(() => {
    const minutes = Math.floor(timer.value / 60)
        .toString()
        .padStart(2, "0");
    const seconds = (timer.value % 60).toString().padStart(2, "0");
    return `${minutes}:${seconds}`;
});

// Timer countdown
let countdownInterval;

const handleUPIReturn = () => {
    if (sessionStorage.getItem("upi_started") === "yes") {
        showUtrPopup.value = true;
    }
};

const checkUPIReturn = () => {
    if (document.visibilityState === "visible") {
        handleUPIReturn();
    }
};

onMounted(() => {
    if (route.query.goodsId) {
        amount.value = parseFloat(route.query.goodsId);
    } else {
        router.push("/mine");
    }

    countdownInterval = setInterval(() => {
        if (timer.value > 0) {
            timer.value--;
        } else {
            clearInterval(countdownInterval);
            router.push("/mine");
        }
    }, 1000);

    getUpiId();
    getQrCode();

    window.addEventListener("focus", handleUPIReturn);
    document.addEventListener("visibilitychange", checkUPIReturn);
});

onUnmounted(() => {
    clearInterval(countdownInterval);
});

// ✅ Fetch UPI ID
const getUpiId = async () => {
    try {
        const res = await axios.get("/get-upi");
        upiId.value = res.data?.data?.UPI_ID || "upi@bank";
    } catch (err) {
        console.error(err);
    }
};

// ✅ Fetch QR Code
const getQrCode = async () => {
    try {
        const res = await axios.get("/get-qr-code");
        qrCodeUrl.value = res.data?.data || "";
    } catch (err) {
        console.error(err);
    }
};

// ✅ Open UPI App
const openUPIApp = () => {
    sessionStorage.setItem("upi_started", "yes");

    const link = `upi://pay?pa=${upiId.value}&pn=Payment&am=${amount.value}&cu=INR`;
    window.location.href = link;
};

// ✅ Copy function
const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(String(text));
        showToast("Copied successfully!");
    } catch {
        showToast("Failed to copy, please copy manually");
    }
};

// ✅ Submit UTR
const submitUTR = async () => {
    if (!utr.value || utr.value.length !== 12 || isNaN(utr.value)) {
        showToast("Invalid UTR (must be 12 digits)");
        return;
    }

    try {
        const res = await axios.post("/recharge", {
            amount: amount.value,
            rechargetype: "UPIPayment",
            reference: utr.value,
        });

        if (res.data?.success) {
            showToast(res.data.message+' asdasd' || "Payment submitted successfully!");
            router.push("/recharge-status?st=success");
            // setTimeout(() => router.push("/mine"), 4000);
        } else {
            showToast(res.data.message+'asd 123' || "Payment failed");
            router.push("/recharge-status?st=fa");
        }
    } catch (err) {
        console.error(err);
        showToast("An error occurred while processing payment");
    }
};

// ✅ Toast
const showToast = (message) => {
    toast.value = { show: true, message };
    setTimeout(() => (toast.value.show = false), 3000);
};
</script>

<style scoped>
.payment-page {
    min-height: 100vh;
    background: #f3fff5;
    padding: 12px 8px;
}

.payment-card {
    width: 100%;
    max-width: 520px;
    margin: auto;
    background: #ffffff;
    border-radius: 26px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0,0,0,0.10);
}

.green-header {
    background:linear-gradient(145deg,#005f2b,#00c853);
    color:white;
    padding:20px 22px 12px;
}

.header-top {
    display:flex;
    justify-content:space-between;
    align-items:center;
}

.small-title {
    margin:0;
    font-size:15px;
    opacity:.95;
}

.green-header h2 {
    margin:4px 0 0;
    font-size:36px;
    font-weight:900;
}

.recharge-text {
    margin:4px 0 0;
    font-size:16px;
    font-weight:600;
}

.timer {
    background:white;
    color:#008f3d;
    padding:12px 18px;
    border-radius:22px;
    font-weight:800;
    font-size:18px;
    text-align:center;
}

.timer small {
    display:block;
    font-size:11px;
    color:#555;
    margin-top:3px;
}

.steps {
    display:flex;
    justify-content:space-around;
    align-items:flex-start;
    margin-top:12px;
    padding:12px 5px 2px;
    text-align:center;
    color:rgba(255,255,255,.75);
    font-size:13px;
}

.steps div {
    width:33%;
    position:relative;
    font-weight:600;
}


.steps div:not(:last-child)::after {
    content:"";
    position:absolute;
    top:17px;
    right:-10%;
    width:20%;
    height:2px;
    background:rgba(255,255,255,.5);
}

.steps b {
    display:flex;
    width:34px;
    height:34px;
    margin:0 auto 8px;
    align-items:center;
    justify-content:center;
    border-radius:50%;
    background:rgba(255,255,255,.25);
    color:white;
    font-size:15px;
}

.steps span {
    display:block;
}

.steps .active b {
    background:white;
    color:#008f3d;
}

.steps .active {
    color:white;
    font-weight:800;
}

.payment-box {
    margin:14px;
    padding:16px;
    border-radius:20px;
    background:#ffffff;
    border:1px solid #dcefe1;
}

.payment-box h4 {
    color:#008f3d;
    font-size:18px;
    font-weight:800;
    margin-bottom:12px;
}


.qr-section {
    text-align:center;
    padding:14px 16px;
}

.qr-subtitle {
    font-size:14px;
    color:#666;
    margin-top:-4px;
    margin-bottom:10px;
}

.qr-warning {
    font-size:12px;
    color:#777;
    margin-top:4px;
}

.qr-image {
    width:190px;
    height:190px;
    object-fit:contain;
    margin:6px auto;
}

.payment-box p {
    font-size:13px;
    color:#777;
    line-height:1.5;
}


.or-divider {
    display:flex;
    align-items:center;
    gap:12px;
    margin:14px 0;
    color:#888;
    font-size:13px;
}

.or-divider span {
    flex:1;
    height:1px;
    background:#ddd;
}

.upi-btn {
    width:100%;
    display:flex;
    align-items:center;
    justify-content:space-between;
    background:#ffffff;
    border:1px solid #d8f3df;
    padding:12px;
    margin-bottom:12px;
    border-radius:16px;
    font-weight:600;
}



.upi-left {
    display:flex;
    align-items:center;
    gap:16px;
}

.upi-left img {
    width:48px;
    height:48px;
    object-fit:contain;
}

.upi-left strong {
    display:block;
    color:#1b2c42;
    font-size:17px;
}

.upi-left small {
    display:block;
    color:#777;
    font-size:13px;
    margin-top:3px;
}

.upi-btn > span {
    font-size:28px;
    color:#008f3d;
}



.copy-row {
    display:flex;
    justify-content:space-between;
    align-items:center;
    background:#ffffff;
    border:1px solid #d8f3df;
    padding:12px;
    border-radius:16px;
    margin-bottom:10px;
    font-weight:600;
}


.copy-info small {
    display:block;
    color:#777;
    font-size:12px;
}

.copy-info strong {
    display:block;
    color:#1b2c42;
    font-size:16px;
    margin-top:3px;
}

.copy-row button {
    background:#008f3d;
    color:#fff;
    border:none;
    border-radius:10px;
    padding:7px 16px;
    font-weight:600;
}



.utr-box {
    margin:16px;
    padding:20px;
    background:#f5fff7;
    border:1px solid #d8f3df;
    border-radius:20px;
}


.utr-box h4 {
    margin:0 0 8px;
    color:#008f3d;
    font-size:18px;
    font-weight:800;
}

.utr-desc {
    margin:0 0 16px;
    color:#777;
    font-size:13px;
    line-height:1.5;
}

.utr-box input {
    width:100%;
    padding:16px;
    border-radius:14px;
    border:1px solid #cfe8d5;
    margin-bottom:15px;
    font-size:15px;
    outline:none;
    background:#ffffff;
}

.submit-btn {
    width:100%;
    padding:16px;
    background:linear-gradient(135deg,#008f3d,#00c853);
    color:white;
    border:0;
    border-radius:16px;
    font-weight:800;
    font-size:17px;
}



.toast-popup {
    position:fixed;
    bottom:180px;
    left:50%;
    transform:translateX(-50%);
    background:#008f3d;
    color:white;
    padding:12px 22px;
    border-radius:25px;
    font-size:14px;
    font-weight:600;
    z-index:9999;
    box-shadow:0 8px 20px rgba(0,0,0,.2);
}


.notice-box {
    margin:16px;
    padding:18px;
    background:#f5fff7;
    border:1px solid #d8f3df;
    border-radius:20px;
}

.notice-box h4 {
    margin:0 0 12px;
    color:#008f3d;
    font-size:18px;
    font-weight:800;
}

.notice-box ol {
    margin:0;
    padding-left:20px;
    list-style: decimal !important;
    color:#555;
    font-size:13px;
    line-height:1.6;
}

.notice-box li {
    margin-bottom:8px;
}


.utr-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
}

.utr-popup {
    width: 100%;
    max-width: 360px;
    background: #ffffff;
    border-radius: 18px;
    padding: 18px;
    text-align: center;
    box-shadow: 0 8px 30px rgba(0,0,0,0.18);
}

.utr-popup h3 {
    background: linear-gradient(135deg,#006b16,#00a52a);
    color: #fff;
    padding: 14px;
    margin: -18px -18px 15px;
    border-radius: 18px 18px 0 0;
    font-size: 17px;
}

.utr-qr {
    width: 180px;
    height: 180px;
    padding: 8px;
    border: 2px solid #00a52a;
    border-radius: 14px;
}

.utr-timer {
    margin: 12px auto;
    background: #eaf7ea;
    color: #008000;
    font-weight: bold;
    padding: 8px 18px;
    border-radius: 20px;
    width: fit-content;
}

.utr-popup input {
    width: 100%;
    box-sizing: border-box;
    padding: 12px;
    border: 1.5px solid #00a000;
    border-radius: 10px;
    margin: 12px 0;
    font-size: 14px;
}

.utr-popup p {
    font-size: 12px;
    color: #555;
    line-height: 1.5;
}

.utr-popup button {
    width: 100%;
    background: #008000;
    color: white;
    border: none;
    padding: 13px;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
}


.utr-popup input:focus {
    border-color: #008000;
    box-shadow: 0 0 8px rgba(0,128,0,0.20);
    outline: none;
}


/* Keep long UPI IDs inside the card */
.copy-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.copy-info {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.copy-info strong {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.copy-row button {
    flex: 0 0 auto;
}

</style>